import { InternalAffairsApi } from '@/api/api.internal-affairs';
import { RaceList } from '@/constants/form_inputs';
import { IACaseType, IACaseStatus, IIACaseUserStub } from '@/models';
import { formatPersonName } from '@/utils';

export const formatCaseType = (caseType: IACaseType | string): string => {
  const typeMap: Record<string, string> = {
    [IACaseType.USE_OF_FORCE]: 'Use of Force',
    [IACaseType.OFFICER_MISCONDUCT]: 'Officer Misconduct',
    [IACaseType.CITIZEN_COMPLAINT]: 'Citizen Complaint',
    [IACaseType.INTERNAL_COMPLAINT]: 'Internal Complaint',
    [IACaseType.POLICY_VIOLATION]: 'Policy Violation',
  };
  return typeMap[caseType] || caseType;
};

export const formatStatus = (status: IACaseStatus | string): string => {
  const statusMap: Record<string, string> = {
    [IACaseStatus.OPEN]: 'Open',
    [IACaseStatus.IN_PROGRESS]: 'In Progress',
    [IACaseStatus.CLOSED]: 'Closed',
  };
  return statusMap[status] || status;
};

export const getUserDisplayName = (
  user: string | IIACaseUserStub | undefined,
): string => {
  if (!user) return 'Unknown';
  if (typeof user === 'string') return user;
  const name = formatPersonName(user);
  return name || user.email || user._id || 'Unknown';
};

export const generateSingleCasePDF = async (
  caseId: string,
  mode: 'download' | 'blob' = 'download',
  exportMeta?: { recipient: string; reason: string },
): Promise<Blob | void> => {
  try {
    const [caseRes, peopleRes] = await Promise.all([
      InternalAffairsApi.getOne(caseId),
      InternalAffairsApi.getPeopleInvolved(caseId, { page: 1, limit: 100 }),
    ]);

    const iaCase = caseRes.data;
    const people = peopleRes.data.results || [];

    const pdfMakeModule = await import('pdfmake/build/pdfmake');
    const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
    const pdfMake = (pdfMakeModule as any).default || pdfMakeModule;
    const pdfFonts = (pdfFontsModule as any).default || pdfFontsModule;
    pdfMake.vfs = pdfFonts.vfs || pdfFonts;

    const formatDateTime = (d: string | undefined) => {
      if (!d) return 'N/A';
      const date = new Date(d);
      if (isNaN(date.getTime())) return d;
      return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(
        date.getDate(),
      ).padStart(2, '0')}/${date.getFullYear()} ${String(
        date.getHours(),
      ).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    };

    const formatDateOnly = (d: string | undefined) => {
      if (!d) return 'N/A';
      const date = new Date(d);
      if (isNaN(date.getTime())) return 'N/A';
      return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(
        date.getDate(),
      ).padStart(2, '0')}/${date.getFullYear()}`;
    };

    const formatGender = (sex: string | undefined) => {
      if (!sex) return 'N/A';
      const genderMap: Record<string, string> = {
        M: 'Male',
        F: 'Female',
        U: 'Unknown',
        O: 'Other',
        male: 'Male',
        female: 'Female',
        unknown: 'Unknown',
        other: 'Other',
      };
      return genderMap[sex] || sex;
    };

    const formatRace = (race: string | undefined) => {
      if (!race) return 'N/A';
      return RaceList[race as keyof typeof RaceList] || race;
    };

    const headerStyle = {
      bold: true,
      fontSize: 8,
      fillColor: '#E8F5E9',
      margin: [2, 4, 2, 4] as [number, number, number, number],
    };
    const cellStyle = {
      fontSize: 8,
      margin: [2, 3, 2, 3] as [number, number, number, number],
    };

    const sectionTitle = (text: string) => ({
      text,
      bold: true,
      fontSize: 12,
      margin: [0, 15, 0, 8] as [number, number, number, number],
    });

    const overviewRows = [
      ['Case ID', iaCase.caseId || 'N/A'],
      ['Case Type', formatCaseType(iaCase.caseType)],
      ['Status', formatStatus(iaCase.status)],
      ['Case Owner', getUserDisplayName(iaCase.caseOwner)],
      ['Created By', getUserDisplayName(iaCase.createdBy)],
      [
        'Assigned Officers',
        iaCase.assignedOfficers?.map((o) => getUserDisplayName(o)).join(', ') ||
          'N/A',
      ],
      ['Created At', formatDateTime(iaCase.createdAt)],
      ['Updated At', formatDateTime(iaCase.updatedAt)],
      ['Case Details', iaCase.caseDetails || 'N/A'],
      ['Officer Details', iaCase.officerDetails || 'N/A'],
      ['Investigation Notes', iaCase.investigationNotes || 'N/A'],
      [
        'Findings & Recommendations',
        iaCase.findingsAndRecommendations || 'N/A',
      ],
    ];

    const peopleHeaders = ['Name', 'DOB', 'Gender', 'Race'].map((h) => ({
      text: h,
      ...headerStyle,
    }));

    const peopleRows = people.map((p: any) => [
      {
        text: formatPersonName(p) || 'N/A',
        ...cellStyle,
      },
      { text: formatDateOnly(p.birthday), ...cellStyle },
      { text: formatGender(p.sex), ...cellStyle },
      { text: formatRace(p.race), ...cellStyle },
    ]);

    const relatedCfs = (iaCase as any).relatedCfs || [];
    const relatedCases = (iaCase as any).relatedCase || [];
    const relatedHeaders = ['Type', 'Record ID', 'Description'].map((h) => ({
      text: h,
      ...headerStyle,
    }));

    const relatedRows = [
      ...relatedCfs.map((cfs: any) => [
        { text: 'CFS', ...cellStyle },
        { text: cfs.number || cfs._id || 'N/A', ...cellStyle },
        { text: cfs.shortDescription || 'N/A', ...cellStyle },
      ]),
      ...relatedCases.map((kase: any) => [
        { text: 'Case', ...cellStyle },
        { text: kase.number || kase._id || 'N/A', ...cellStyle },
        { text: 'N/A', ...cellStyle },
      ]),
    ];

    const content: any[] = [
      {
        text: `Internal Affairs Case Report`,
        alignment: 'center',
        bold: true,
        fontSize: 16,
        margin: [0, 0, 0, 5] as [number, number, number, number],
      },
      {
        text: iaCase.caseId || '',
        alignment: 'center',
        fontSize: 11,
        color: '#666',
        margin: [0, 0, 0, 15] as [number, number, number, number],
      },
    ];

    // Export metadata (if provided)
    if (exportMeta) {
      content.push({
        columns: [
          { text: 'Recipient:', bold: true, fontSize: 9, width: 'auto' },
          { text: exportMeta.recipient, fontSize: 9 },
        ],
        columnGap: 4,
        margin: [0, 0, 0, 4] as [number, number, number, number],
      });
      content.push({
        columns: [
          { text: 'Reason:', bold: true, fontSize: 9, width: 'auto' },
          { text: exportMeta.reason, fontSize: 9 },
        ],
        columnGap: 4,
        margin: [0, 0, 0, 12] as [number, number, number, number],
      });
    }

    content.push(sectionTitle('Case Overview'));
    content.push({
      table: {
        widths: [130, '*'],
        body: overviewRows.map(([label, value]) => [
          {
            text: label,
            bold: true,
            fontSize: 8,
            fillColor: '#f5f5f5',
            margin: [4, 3, 4, 3] as [number, number, number, number],
          },
          {
            text: value || '',
            fontSize: 8,
            margin: [4, 3, 4, 3] as [number, number, number, number],
          },
        ]),
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#ddd',
        vLineColor: () => '#ddd',
      },
    });

    content.push(sectionTitle('People Involved'));
    if (peopleRows.length > 0) {
      content.push({
        table: {
          headerRows: 1,
          widths: ['*', '*', '*', '*'],
          body: [peopleHeaders, ...peopleRows],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#ddd',
          vLineColor: () => '#ddd',
        },
      });
    } else {
      content.push({
        text: 'No people involved.',
        fontSize: 9,
        color: '#999',
        margin: [0, 0, 0, 10] as [number, number, number, number],
      });
    }

    content.push(sectionTitle('Related Records'));
    if (relatedRows.length > 0) {
      content.push({
        table: {
          headerRows: 1,
          widths: [60, '*', '*'],
          body: [relatedHeaders, ...relatedRows],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#ddd',
          vLineColor: () => '#ddd',
        },
      });
    } else {
      content.push({
        text: 'No related records.',
        fontSize: 9,
        color: '#999',
        margin: [0, 0, 0, 10] as [number, number, number, number],
      });
    }

    const definition = {
      pageSize: 'A4',
      pageMargins: [30, 30, 30, 50],
      content,
      footer: () => ({
        text: '911Systems »',
        alignment: 'center',
        fontSize: 9,
        color: '#666',
        margin: [30, 10, 30, 0] as [number, number, number, number],
      }),
    };

    const createPdf = pdfMake.createPdf || pdfMake;
    const pdfDoc = createPdf(definition);
    const fileName = `${iaCase.caseId || 'IA_Case'}.pdf`;

    if (mode === 'blob') {
      return await new Promise<Blob>((resolve, reject) => {
        pdfDoc.getBlob((blob: Blob) => {
          if (blob.size === 0) {
            reject(new Error('Generated PDF is empty'));
            return;
          }
          resolve(blob);
        });
      });
    }

    pdfDoc.download(fileName);
  } catch (error) {
    console.error('Error generating single case PDF:', error);
  }
};
